<template>
  <section class="ncrm-file-request-editor">
    <div class="ncrm-file-request-editor__panel">
      <div class="ncrm-file-request-editor__section-header">
        <div>
          <div class="text-body-2 font-weight-semibold ncrm-text-strong">
            {{ $t("ncrm.workflows.nodeSettings.externalForms.fileRequestCopyTitle") }}
          </div>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t("ncrm.workflows.nodeSettings.externalForms.fileRequestCopyHint") }}
          </div>
        </div>
        <v-switch
          v-model="form.consent.required"
          color="primary"
          density="compact"
          hide-details
          :label="$t('ncrm.workflows.nodeSettings.externalForms.consentRequired')"
        />
      </div>

      <div
        class="ncrm-file-request-editor__language-list"
        role="group"
        :aria-label="$t('ncrm.workflows.nodeSettings.externalForms.fileRequestCopyTitle')"
      >
        <v-btn
          v-for="locale in localeOptions"
          :key="locale.value"
          :variant="activeLocale === locale.value ? 'tonal' : 'outlined'"
          :color="activeLocale === locale.value ? 'primary' : 'neutral'"
          :aria-pressed="activeLocale === locale.value"
          @click="activeLocale = locale.value"
        >
          {{ locale.label }}
        </v-btn>
      </div>

      <div class="ncrm-file-request-editor__fields">
        <v-text-field v-model="localizedContent.eyebrow" :label="$t('ncrm.workflows.nodeSettings.externalForms.fileRequestEyebrow')" hide-details="auto" />
        <v-text-field v-model="localizedContent.title" :label="$t('ncrm.workflows.nodeSettings.externalForms.title')" hide-details="auto" />
        <v-textarea class="ncrm-file-request-editor__full" v-model="localizedContent.subtitle" :label="$t('ncrm.workflows.nodeSettings.externalForms.subtitle')" rows="2" hide-details="auto" />
        <v-text-field v-model="localizedContent.nameLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.nameFieldLabel')" hide-details="auto" />
        <v-text-field v-model="localizedContent.namePlaceholder" :label="$t('ncrm.workflows.nodeSettings.externalForms.namePlaceholder')" hide-details="auto" />
        <v-text-field v-model="localizedContent.phoneLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.phoneFieldLabel')" hide-details="auto" />
        <v-text-field v-model="localizedContent.phonePlaceholder" :label="$t('ncrm.workflows.nodeSettings.externalForms.phonePlaceholder')" hide-details="auto" />
        <v-text-field v-model="localizedContent.contentLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.contentFieldLabel')" hide-details="auto" />
        <v-text-field v-model="localizedContent.contentPlaceholder" :label="$t('ncrm.workflows.nodeSettings.externalForms.contentPlaceholder')" hide-details="auto" />
        <v-textarea class="ncrm-file-request-editor__full" v-model="localizedContent.consentLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.consentText')" rows="2" hide-details="auto" />
        <v-text-field v-model="localizedContent.submitLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.submitLabel')" hide-details="auto" />
      </div>
    </div>

    <div class="ncrm-file-request-editor__panel">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.allowedFileFormats") }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.allowedFileFormatsHint") }}
        </div>
      </div>

      <div
        class="ncrm-file-request-editor__format-list"
        role="group"
        :aria-label="$t('ncrm.workflows.nodeSettings.externalForms.allowedFileFormats')"
      >
        <v-chip
          v-for="format in formatOptions"
          :key="format.value"
          :variant="isFormatSelected(format.value) ? 'tonal' : 'outlined'"
          :color="isFormatSelected(format.value) ? 'primary' : undefined"
          :prepend-icon="isFormatSelected(format.value) ? 'mdi-check' : undefined"
          :disabled="isFormatSelected(format.value) && form.displayConfig.allowedFileExtensions.length === 1"
          :aria-pressed="isFormatSelected(format.value)"
          size="default"
          @click="toggleFormat(format.value)"
        >
          {{ format.label }}
        </v-chip>
      </div>

      <v-divider />

      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.previewLanguage") }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ $t("ncrm.workflows.nodeSettings.externalForms.previewLanguageHint") }}
        </div>
      </div>
      <div
        class="ncrm-file-request-editor__language-list"
        role="group"
        :aria-label="$t('ncrm.workflows.nodeSettings.externalForms.previewLanguage')"
      >
        <v-btn
          v-for="locale in localeOptions"
          :key="`preview-${locale.value}`"
          size="small"
          :variant="form.fileRequestPreviewLocale === locale.value ? 'tonal' : 'text'"
          :color="form.fileRequestPreviewLocale === locale.value ? 'primary' : 'neutral'"
          :aria-pressed="form.fileRequestPreviewLocale === locale.value"
          @click="form.fileRequestPreviewLocale = locale.value"
        >
          {{ locale.label }}
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script>
import {
  FILE_REQUEST_FORMATS,
  FILE_REQUEST_LOCALE_LABELS,
  FILE_REQUEST_LOCALES,
  normalizeFileRequestFormats,
  normalizeFileRequestLocales,
} from "@/Utile/ncrmExternalFileFormConfig.mjs";

export default {
  name: "NcrmWorkflowFileRequestContentEditor",
  props: {
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { activeLocale: "ro" };
  },
  computed: {
    localeOptions() {
      return FILE_REQUEST_LOCALES.map((locale) => ({
        value: locale,
        label: FILE_REQUEST_LOCALE_LABELS[locale],
      }));
    },
    formatOptions() {
      return FILE_REQUEST_FORMATS.map((extension) => ({
        value: extension,
        label: extension.slice(1).toUpperCase(),
      }));
    },
    localizedContent() {
      return this.form.content.fileRequestLocales[this.activeLocale];
    },
  },
  methods: {
    isFormatSelected(extension) {
      return this.form.displayConfig.allowedFileExtensions.includes(extension);
    },
    toggleFormat(extension) {
      const selected = this.form.displayConfig.allowedFileExtensions;
      if (selected.includes(extension)) {
        if (selected.length > 1) {
          this.form.displayConfig.allowedFileExtensions = selected.filter((item) => item !== extension);
        }
        return;
      }
      this.form.displayConfig.allowedFileExtensions = FILE_REQUEST_FORMATS.filter(
        (item) => item === extension || selected.includes(item)
      );
    },
  },
  watch: {
    form: {
      immediate: true,
      handler(form) {
        form.content.fileRequestLocales = normalizeFileRequestLocales(form.content.fileRequestLocales);
        form.displayConfig.allowedFileExtensions = normalizeFileRequestFormats(
          form.displayConfig.allowedFileExtensions
        );
        if (!FILE_REQUEST_LOCALES.includes(form.fileRequestPreviewLocale)) {
          form.fileRequestPreviewLocale = "ro";
        }
      },
    },
  },
};
</script>

<style scoped src="./NcrmWorkflowFileRequestContentEditor.css"></style>
